/*
 * Impulse header: category panels that open on hover (desktop), like Pandora.
 * - mouse: opens on hover, closes shortly after the pointer leaves the item and its panel
 * - keyboard: opens on focus, closes on Escape or when focus leaves
 * - touch (tablets in landscape): the first tap opens the panel, the second tap follows the link
 * - thumbnails are placeholders in the HTML and get their <img> only when a panel or sub menu opens
 */
(() => {
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 220;
  const root = document.documentElement;

  // Adds the images of a closed menu the first time it is shown (they are decorative, links stay in the HTML).
  function hydrateThumbs(scope) {
    scope.querySelectorAll('[data-menu-thumb]').forEach((el) => {
      const img = document.createElement('img');
      img.src = el.dataset.src;
      if (el.dataset.srcset) img.srcset = el.dataset.srcset;
      img.width = Number(el.dataset.width) || 44;
      img.height = Number(el.dataset.height) || 44;
      img.alt = '';
      img.decoding = 'async';
      if (el.classList.contains('header-menu-link__thumb')) {
        el.removeAttribute('data-menu-thumb');
        el.appendChild(img);
      } else {
        el.replaceWith(img);
      }
    });
  }

  function init(scope = document) {
    const items = [...scope.querySelectorAll('[data-impulse-mega]')].filter((item) => !item.dataset.megaReady);
    if (!items.length) return;

    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    const parts = (item) => ({
      trigger: item.querySelector('[data-impulse-mega-trigger]'),
      panel: item.querySelector('[data-impulse-mega-panel]'),
    });

    function close(item) {
      if (!item) return;
      const { trigger, panel } = parts(item);
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      if (openItem === item) openItem = null;
      if (!openItem) root.classList.remove('impulse-mega-open');
    }

    function open(item) {
      hydrateThumbs(item);
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) close(openItem);
      const { trigger, panel } = parts(item);
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      openItem = item;
      root.classList.add('impulse-mega-open');
    }

    const scheduleOpen = (item) => {
      hydrateThumbs(item);
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      // Moving between two items should switch instantly, a first hover waits a moment.
      openTimer = setTimeout(() => open(item), openItem ? 0 : OPEN_DELAY);
    };

    const scheduleClose = (item) => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => close(item), CLOSE_DELAY);
    };

    items.forEach((item) => {
      item.dataset.megaReady = 'true';
      const { trigger, panel } = parts(item);

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        scheduleOpen(item);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        scheduleClose(item);
      });

      item.addEventListener('focusin', () => open(item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) scheduleClose(item);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !item.classList.contains('is-open')) return;
        close(item);
        trigger.focus();
      });

      trigger.addEventListener('click', (event) => {
        // Touch: first tap opens the panel, the second one navigates.
        if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) {
          event.preventDefault();
          open(item);
        }
      });

      panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) close(item);
      });
    });

    // Tapping outside closes the panel on touch devices; scrolling closes it everywhere.
    document.addEventListener('pointerdown', (event) => {
      if (openItem && !openItem.contains(event.target)) close(openItem);
    });
    window.addEventListener(
      'scroll',
      () => {
        if (openItem) close(openItem);
      },
      { passive: true }
    );
  }

  // Mobile drawer: the header turns white while the menu is open (same as the desktop panels).
  function watchDrawer() {
    const drawer = document.querySelector('#Details-menu-drawer-container');
    if (!drawer || drawer.dataset.megaWatched) return;
    drawer.dataset.megaWatched = 'true';
    const sync = () => root.classList.toggle('impulse-drawer-open', drawer.hasAttribute('open'));
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  }

  // Mobile drawer: every category is a <details>, fill its thumbnails when it opens.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details.tagName === 'DETAILS' && details.open && details.id !== 'Details-menu-drawer-container' && details.id.startsWith('Details-menu-drawer-')) {
        hydrateThumbs(details);
      }
    },
    true
  );

  init();
  watchDrawer();
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    watchDrawer();
  });
})();
